import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles, Moon, Sun, Flower, Star, Rainbow, Router as Butterfly } from 'lucide-react';

interface EmotionalMoment {
  id: string;
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  color: string;
  bgColor: string;
  story: string;
}

const emotionalMoments: EmotionalMoment[] = [
  {
    id: 'confidence',
    icon: Star,
    title: "Confidence in Your Body",
    description: "Understanding your cycle brings self-awareness and confidence",
    color: "text-yellow-600",
    bgColor: "from-yellow-100 to-orange-100",
    story: "\"For the first time, I felt like I truly understood my body. THREEDOT helped me see patterns I never noticed and gave me the confidence to advocate for my health.\""
  },
  {
    id: 'empowerment',
    icon: Sparkles,
    title: "Empowered Choices",
    description: "Make informed decisions about your health and lifestyle",
    color: "text-purple-600",
    bgColor: "from-purple-100 to-pink-100",
    story: "\"Knowledge is power. Now I can plan my workouts, important meetings, and even vacations around my cycle. I'm in control of my life again.\""
  },
  {
    id: 'peace',
    icon: Moon,
    title: "Peace of Mind",
    description: "No more surprises - know what to expect and when",
    color: "text-blue-600",
    bgColor: "from-blue-100 to-indigo-100",
    story: "\"The anxiety of not knowing when my period would come is gone. I sleep better knowing I'm prepared and my body is healthy.\""
  },
  {
    id: 'connection',
    icon: Heart,
    title: "Deeper Self-Connection",
    description: "Connect with your body's natural rhythm and wisdom",
    color: "text-pink-600",
    bgColor: "from-pink-100 to-rose-100",
    story: "\"I've learned to honor my body's needs throughout my cycle. Some days I'm energetic, others I need rest - and that's perfectly normal.\""
  }
];

const EmotionalConnection: React.FC = () => {
  const [activeStory, setActiveStory] = useState<string | null>(null);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <motion.div
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-100 to-purple-100 rounded-full px-4 py-2 mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Heart className="w-4 h-4 text-pink-500" />
          <span className="text-sm font-medium text-gray-700">Your Wellness Journey</span>
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          More Than Just Tracking
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          THREEDOT is about empowering you to understand, embrace, and celebrate your unique cycle
        </p>
      </div>

      {/* Emotional Moments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {emotionalMoments.map((moment, index) => (
          <motion.div
            key={moment.id}
            className={`relative p-6 bg-gradient-to-br ${moment.bgColor} rounded-2xl border border-white/50 cursor-pointer overflow-hidden`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5, scale: 1.02 }}
            onClick={() => setActiveStory(activeStory === moment.id ? null : moment.id)}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 opacity-20">
              <Butterfly className="w-full h-full text-gray-400" />
            </div>

            <div className="relative z-10">
              <motion.div
                className={`w-12 h-12 mb-4 bg-white rounded-full flex items-center justify-center shadow-lg`}
                whileHover={{ rotate: 10, scale: 1.1 }}
              >
                <moment.icon className={`w-6 h-6 ${moment.color}`} />
              </motion.div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {moment.title}
              </h3>
              <p className="text-gray-700 mb-4">
                {moment.description}
              </p>

              <AnimatePresence>
                {activeStory === moment.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-white/50"
                  >
                    <blockquote className="text-gray-800 italic leading-relaxed">
                      {moment.story}
                    </blockquote>
                    <div className="flex items-center space-x-2 mt-3">
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <span className="text-xs text-gray-600">Real user experience</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div
                className="flex items-center space-x-2 text-sm font-medium text-gray-600 mt-4"
                whileHover={{ x: 4 }}
              >
                <span>Read story</span>
                <motion.div
                  animate={{ x: activeStory === moment.id ? 0 : [0, 4, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  →
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Inspirational Quote Section */}
      <motion.div
        className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-3xl p-8 text-white overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-32 h-32 opacity-20">
          <Flower className="w-full h-full" />
        </div>
        <div className="absolute bottom-0 right-0 w-40 h-40 opacity-10">
          <Rainbow className="w-full h-full" />
        </div>

        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <motion.div
            className="w-16 h-16 mx-auto mb-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
            whileHover={{ scale: 1.1, rotate: 10 }}
          >
            <Heart className="w-8 h-8 text-white" />
          </motion.div>

          <blockquote className="text-2xl font-light leading-relaxed mb-6">
            "Your cycle is not something to endure—it's something to understand, embrace, and celebrate. Every woman deserves to feel empowered by her body's natural wisdom."
          </blockquote>

          <div className="flex items-center justify-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-lg">👩🏽‍⚕️</span>
            </div>
            <div className="text-left">
              <p className="font-medium">Dr. Sarah Chen</p>
              <p className="text-sm opacity-90">Reproductive Health Specialist</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Community Connection */}
      <motion.div
        className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <div className="text-center">
          <motion.div
            className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center"
            whileHover={{ scale: 1.1, rotate: 10 }}
          >
            <Sparkles className="w-8 h-8 text-white" />
          </motion.div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Join Our Supportive Community
          </h3>
          <p className="text-gray-600 mb-6">
            Connect with women who understand your journey. Share experiences, ask questions, and celebrate milestones together.
          </p>

          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="flex -space-x-2">
              {['👩🏻‍💼', '👩🏽‍⚕️', '👩🏼‍🎓', '👩🏿‍💻', '👩🏻‍🏫'].map((avatar, i) => (
                <motion.div
                  key={i}
                  className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center border-2 border-white"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                  whileHover={{ scale: 1.1, zIndex: 10 }}
                >
                  <span className="text-sm">{avatar}</span>
                </motion.div>
              ))}
            </div>
            <span className="text-sm text-gray-600">+50,000 women worldwide</span>
          </div>

          <motion.button
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Join the Community
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default EmotionalConnection;